

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tracks a Retry-After countdown for rate-limit 429 responses.
 *
 * Call `start(seconds)` when an API error arrives with a Retry-After header;
 * `secondsLeft` ticks down once per second and `isLimited` flips to false when
 * it reaches zero. Forms can use `isLimited` to disable submit buttons and
 * render a live countdown message.
 */
export function useRateLimitCountdown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds: number) => {
      clear();
      if (!Number.isFinite(seconds) || seconds <= 0) {
        setSecondsLeft(0);
        return;
      }
      setSecondsLeft(Math.ceil(seconds));
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clear();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clear],
  );

  useEffect(() => clear, [clear]);

  return { secondsLeft, isLimited: secondsLeft > 0, start, clear };
}
